"use client";

import { useState } from "react";

function formatResetAt(secondsUntilReset: number | null, now: number): string | null {
  if (!secondsUntilReset || secondsUntilReset <= 0) return null;

  const resetAt = new Date(now + secondsUntilReset * 1000);
  return resetAt.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function CompactUsageBar({
  label,
  used,
  limit,
  resetInSeconds = null,
}: {
  label: string;
  used: number;
  limit: number | null;
  resetInSeconds?: number | null;
}) {
  // Capture a stable timestamp via useState initializer (runs once per mount)
  // to avoid calling Date.now() during render
  const [mountTime] = useState(() => Date.now());

  if (limit === null) {
    return (
      <div className="space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span className="text-xs font-semibold text-purple-400">∞</span>
        </div>
      </div>
    );
  }

  const percentage = Math.min((used / limit) * 100, 100);
  let colorClass = "bg-green-500";
  if (percentage > 90) colorClass = "bg-red-500";
  else if (percentage > 70) colorClass = "bg-yellow-500";
  const resetAtFormatted = formatResetAt(resetInSeconds, mountTime);

  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{Math.round(percentage)}%</span>
      </div>
      <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${colorClass}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {resetAtFormatted && (
        <div className="text-[10px] text-muted-foreground/70">Resets at {resetAtFormatted}</div>
      )}
    </div>
  );
}
